// Libs
import React, { useState, useContext, useEffect } from "react";
import { useObserver, KeyValuePair, FieldType, Else, TypographyX } from "@shoothill/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { FormLabel, Box, Typography, Checkbox, FormControlLabel, Radio, FormControl, FormHelperText, Select, InputAdornment, TextField, useMediaQuery } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";

// App
import { StoresContext, Stores } from "Globals/Stores";

// Models
import { HomeOwnerStatusEnum } from "Globals/Models/HomeOwnerStatusEnum";
import { PropertyDetailsModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PropertyDetailsModel";

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";

// Views
import { CurrencyInput } from "Globals/Views/CurrencyInput";
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";
import { CustomDatePicker } from "Globals/Views/CustomDatePicker";

// Styles
import { theme } from "Globals/Styles/AppTheme";
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { CustomTwoSelects } from "Globals/Styles/CustomTwoSelects";
import { Row } from "Globals/Styles/LayoutStyles";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import { CustomInput } from "Globals/Styles/AppStyling";

interface IProps {
    applicant: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const BackgroundInformationView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(props.applicant.backgroundInformationViewModel);
    const isMobile: boolean = useMediaQuery("screen and (max-width: 767px)") ? true : false;

    return useObserver(() => (
        <div
            onKeyUp={() => {
                viewModel.restartTimer();
            }}
        >
            {viewModel.DevTool_ShowCanProceed && <h5 style={{ color: "red" }}>Can Proceed: {viewModel.canProceed ? "Yes" : "No"}</h5>}
            <Row>
                <CustomYesNoRadio
                    label="Are you a British or EU / EEA citizen?"
                    fieldName={"isBritishOrEUCitizen"}
                    value={viewModel.model.isBritishOrEUCitizen}
                    onChange={viewModel.handleRadioChange}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("isBritishOrEUCitizen")}
                />
                {viewModel.model.isBritishOrEUCitizen === false && (
                    <CustomYesNoRadio
                        label="Is your passport stamped with indefinite leave to remain?"
                        fieldName={"isPassportIndefinite"}
                        value={viewModel.model.isPassportIndefinite}
                        onChange={viewModel.handleRadioChange}
                        disabled={props.applicationFormViewModel.model.isSubmitted}
                        error={viewModel.getError("isPassportIndefinite")}
                    />
                )}
            </Row>
            <Row>
                {viewModel.model.isBritishOrEUCitizen === false && viewModel.model.isPassportIndefinite === false && (
                    <CustomDatePicker
                        viewModel={viewModel}
                        label="When does your leave to remain / work visa end?"
                        placeholder={"Enter date"}
                        fieldName="visaEndDate"
                        value={viewModel.model.visaEndDate}
                        onChange={(date) => {
                            viewModel.setDate("visaEndDate", date);
                            viewModel.restartTimer();
                        }}
                        resetCallback={viewModel.restartTimer}
                        disabled={props.applicationFormViewModel.model.isSubmitted}
                        error={viewModel.getError("visaEndDate")}
                    />
                )}
            </Row>
            <Row>
                <Typography variant="h6">Equal opportunities</Typography>
            </Row>
            <Row style={{ marginTop: "0px", padding: "0px" }}>
                <Typography variant="body1" style={{ fontSize: "14px" }}>
                    The information in the equalities section is requested to enable Hightown to make sure we treat all applicants fairly. It is also used for statistical returns
                    to Government agencies. All personal data provided is treated in confidence. Providing the information is optional and your application will not be affected if
                    you choose not to provide the information. Please tick one box for each applicant in each section.
                </Typography>
            </Row>
            <Row>
                <CustomInput
                    type="select"
                    label="Age"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    viewModel={viewModel}
                    fieldName="ageId"
                    selectItems={storesContext.Domain.AgeStore.getAgeOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    label="Gender"
                    viewModel={viewModel}
                    fieldName="gender"
                    selectItems={storesContext.Domain.SelectItemsStore.getGenderOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    type="select"
                    label="Is your present gender the same as you were assigned at birth?"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    viewModel={viewModel}
                    fieldName="genderAtBirth"
                    selectItems={storesContext.Domain.SelectItemsStore.getGenderSameAsBithOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    label="Ethnicity"
                    viewModel={viewModel}
                    fieldName="ethnicityId"
                    selectItems={storesContext.Domain.EthnicityStore.getEthnicityOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                {storesContext.Domain.EthnicityStore.getRequiresOther(viewModel.model.ethnicityId) == true && (
                    <CustomInput
                        label="Please state"
                        placeHolder="Enter ethnicity"
                        viewModel={viewModel}
                        fieldName="ethnicityOther"
                        shrink={true}
                        onChange={() => viewModel.handleInputChange()}
                        inputProps={{
                            disabled: props.applicationFormViewModel.model.isSubmitted,
                        }}
                    />
                )}
            </Row>
            <Row>
                <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    label="Sexual orientation"
                    viewModel={viewModel}
                    fieldName="sexualOrientationId"
                    selectItems={storesContext.Domain.SexualOrientationStore.getSexualOrientationOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    label="Religion"
                    viewModel={viewModel}
                    fieldName="religionId"
                    selectItems={storesContext.Domain.ReligionStore.getReligionOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    label="Disability"
                    viewModel={viewModel}
                    fieldName="disabilityId"
                    selectItems={storesContext.Domain.DisabilityStore.getDisabilityOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                {storesContext.Domain.DisabilityStore.getDisabilityOptions.find((d) => d.value === viewModel.getValue("disabilityId")) ? (
                    storesContext.Domain.DisabilityStore.getDisabilityOptions
                        .find((d) => d.value === viewModel.getValue("disabilityId"))!
                        .key.toLocaleLowerCase()
                        .includes("other") ? (
                        <CustomInput
                            label="Please state"
                            placeHolder="Enter disability"
                            viewModel={viewModel}
                            fieldName="disabilityOther"
                            shrink={true}
                            onChange={() => viewModel.handleInputChange()}
                            inputProps={{
                                disabled: props.applicationFormViewModel.model.isSubmitted,
                            }}
                        />
                    ) : (
                        <></>
                    )
                ) : (
                    <></>
                )}
                <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    label="Special requirements"
                    viewModel={viewModel}
                    fieldName="specialRequirementsId"
                    selectItems={storesContext.Domain.SpecialRequirementsStore.getSpecialRequirementsOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    label="Nationality"
                    viewModel={viewModel}
                    fieldName="nationalityId"
                    selectItems={storesContext.Domain.NationalityStore.getNationalityOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
        </div>
    ));
};
